import {
  DeesElement,
  html,
  customElement,
  type TemplateResult,
  property,
  state,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { viewerStyles } from './styles.js';
import { mediaControls } from '../viewer.styles.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import { demo } from './demo.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-audio-viewer': DeesAudioViewer;
  }
}

@customElement('dees-audio-viewer')
export class DeesAudioViewer extends DeesElement {
  public static styles = [themeDefaultStyles, viewerStyles, mediaControls];
  public static demo = demo;
  public static demoGroups = ['Media'];

  @property()
  accessor src: string = '';

  @property()
  accessor title: string = '';

  @property()
  accessor artist: string = '';

  @property({ type: Boolean })
  accessor showWaveform: boolean = true;

  @property({ type: Boolean })
  accessor autoplay: boolean = false;

  @property({ type: Boolean })
  accessor loop: boolean = false;

  @state()
  accessor isPlaying: boolean = false;

  @state()
  accessor currentTime: number = 0;

  @state()
  accessor duration: number = 0;

  @state()
  accessor volume: number = 1;

  @state()
  accessor isMuted: boolean = false;

  @state()
  accessor loading: boolean = false;

  @state()
  accessor error: string = '';

  @state()
  accessor waveformData: number[] = [];

  @state()
  accessor waveformReady: boolean = false;

  private audioElement: HTMLAudioElement | null = null;
  private mediaAbort?: AbortController;
  private waveformContext?: AudioContext;
  private resizeObserver?: ResizeObserver;
  @state() private accessor waveformError = '';
  @state() private accessor playbackError = '';
  private volumeBeforeMute: number = 1;

  public render(): TemplateResult {
    const titleText = this.title && this.artist
      ? `${this.title} — ${this.artist}`
      : this.title || this.artist || '';

    return html`
      <div class="viewer-container">
        <div class="toolbar">
          <div class="toolbar-group">
            <button type="button" class="toolbar-button" aria-label=${this.isPlaying ? 'Pause' : 'Play'} ?disabled=${!this.src || this.loading || !!this.error} @click=${this.togglePlay}>
              <dees-icon icon="lucide:${this.isPlaying ? 'Pause' : 'Play'}"></dees-icon>
            </button>
            <span class="time-display">
              ${this.formatTime(this.currentTime)} / ${this.formatTime(this.duration)}
            </span>
          </div>

          ${titleText ? html`
            <span class="toolbar-title">${titleText}</span>
          ` : ''}

          <div class="toolbar-group--end">
            <button type="button"
              class="toolbar-button ${this.loop ? 'active' : ''}"
              @click=${this.toggleLoop}
              title="Loop" aria-pressed=${this.loop}
            >
              <dees-icon icon="lucide:Repeat"></dees-icon>
            </button>

            <div class="volume-group">
              <button type="button" class="toolbar-button" @click=${this.toggleMute} title="${this.isMuted ? 'Unmute' : 'Mute'}">
                <dees-icon icon="lucide:${this.isMuted || this.volume === 0 ? 'VolumeX' : this.volume < 0.5 ? 'Volume1' : 'Volume2'}"></dees-icon>
              </button>
              <input
                class="volume-slider" aria-label="Volume"
                type="range"
                min="0"
                max="1"
                step="0.01"
                .value=${String(this.isMuted ? 0 : this.volume)}
                @input=${this.handleVolumeChange}
              />
            </div>
          </div>
        </div>

        <div class="content-area">
          ${this.error ? html`
            <div class="error-overlay" role="alert">
              <dees-icon class="error-icon" icon="lucide:MusicOff"></dees-icon>
              <span class="error-text">${this.error}</span>
            </div>
          ` : this.loading ? html`
            <div class="loading-overlay" role="status" aria-label="Loading audio">
              <div class="loading-spinner"></div>
            </div>
          ` : html`
            ${this.showWaveform && this.waveformReady ? html`<div class="waveform-container" @click=${this.handleWaveformClick}>
              <canvas aria-hidden="true"></canvas><span class="waveform-playhead" style=${`left:${this.duration ? this.currentTime / this.duration * 100 : 0}%`}></span>
            </div>` : html`<dees-icon class="audio-symbol" icon="lucide:AudioLines"></dees-icon>`}
            ${this.playbackError ? html`<span role="status" class="waveform-note">${this.playbackError}</span>` : ''}
            ${this.waveformError ? html`<span class="waveform-note">${this.waveformError}</span>` : ''}
            <input class="media-seek" aria-label="Playback position" type="range" min="0" .max=${String(this.duration || 0)} step="0.1"
              .value=${String(this.currentTime)} ?disabled=${!this.duration} @input=${(event: Event) => this.seek(Number((event.target as HTMLInputElement).value))}>
          `}
        </div>
      </div>
    `;
  }

  public async connectedCallback(): Promise<void> {
    await super.connectedCallback();
    if (!this.isConnected) return;
    if (this.src && !this.audioElement) this.initAudio();
    this.resizeObserver = new ResizeObserver(() => this.drawWaveform());
    this.resizeObserver.observe(this);
  }

  public async disconnectedCallback(): Promise<void> {
    this.cleanup();
    this.resizeObserver?.disconnect();
    await super.disconnectedCallback();
  }

  public async updated(changedProperties: Map<PropertyKey, unknown>): Promise<void> {
    super.updated(changedProperties);
    if (changedProperties.has('src') && this.isConnected) {
      if (!this.audioElement || this.audioElement.getAttribute('src') !== this.src) {
        this.cleanup();
        if (this.src) this.initAudio();
      }
    }
    if (changedProperties.has('loop') && this.audioElement) this.audioElement.loop = this.loop;
    if (changedProperties.has('showWaveform') && this.showWaveform && this.audioElement && !this.waveformReady) void this.loadWaveform();
    if (['waveformData', 'waveformReady', 'loading', 'goBright'].some(key => changedProperties.has(key))) this.drawWaveform();
  }

  public play(): void {
    const audio = this.audioElement;
    this.playbackError = '';
    void audio?.play().catch(() => { if (audio === this.audioElement) this.playbackError = 'Playback could not start. Try again.'; });
  }

  public pause(): void {
    this.audioElement?.pause();
  }

  public togglePlay(): void {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  public seek(time: number): void {
    if (this.audioElement) {
      this.audioElement.currentTime = Math.max(0, Math.min(this.duration || 0, time));
      this.currentTime = this.audioElement.currentTime;
    }
  }

  public setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.audioElement) {
      this.audioElement.volume = this.volume;
    }
    if (this.volume > 0) {
      this.isMuted = false;
    }
  }

  public toggleMute(): void {
    if (this.isMuted) {
      this.isMuted = false;
      this.volume = this.volumeBeforeMute || 0.5;
      if (this.audioElement) {
        this.audioElement.volume = this.volume;
      }
    } else {
      this.volumeBeforeMute = this.volume;
      this.isMuted = true;
      if (this.audioElement) {
        this.audioElement.volume = 0;
      }
    }
  }

  private toggleLoop(): void {
    this.loop = !this.loop;
    if (this.audioElement) {
      this.audioElement.loop = this.loop;
    }
  }

  private initAudio(): void {
    const audio = this.audioElement = new Audio();
    const abort = this.mediaAbort = new AbortController();
    this.loading = true;
    this.error = '';
    audio.crossOrigin = 'anonymous';
    audio.src = this.src;
    audio.volume = this.isMuted ? 0 : this.volume;
    audio.loop = this.loop;
    const listen = (name: string, callback: () => void) => audio.addEventListener(name, callback, { signal: abort.signal });
    listen('loadedmetadata', () => { this.duration = Number.isFinite(audio.duration) ? audio.duration : 0; this.loading = false; });
    listen('play', () => { this.isPlaying = true; });
    listen('pause', () => { this.isPlaying = false; });
    listen('ended', () => { this.isPlaying = false; });
    listen('error', () => { this.error = 'Could not load this audio file.'; this.loading = false; });
    listen('timeupdate', () => { this.currentTime = audio.currentTime; });
    if (this.autoplay) void audio.play().catch(() => { /* User activation may be required. */ });
    if (this.showWaveform) void this.loadWaveform();
  }

  private waveformLoading = false;
  private async loadWaveform(): Promise<void> {
    const abort = this.mediaAbort;
    if (!abort || abort.signal.aborted || this.waveformLoading) return;
    this.waveformLoading = true;
    let context: AudioContext | undefined;
    try {
      const response = await fetch(this.src, { signal: abort.signal });
      if (!response.ok) throw new Error('Waveform download failed');
      const buffer = await response.arrayBuffer();
      if (abort.signal.aborted) return;
      context = this.waveformContext = new AudioContext();
      const audioBuffer = await context.decodeAudioData(buffer);
      if (abort.signal.aborted || !this.isConnected) return;
      const samples = audioBuffer.getChannelData(0);
      const bars = Math.min(160, samples.length);
      const waveform = Array.from({ length: bars }, (_, index) => {
        const start = Math.floor(index * samples.length / bars);
        const end = Math.floor((index + 1) * samples.length / bars);
        let sum = 0;
        for (let sample = start; sample < end; sample++) sum += Math.abs(samples[sample]);
        return sum / Math.max(1, end - start);
      });
      const max = Math.max(...waveform);
      this.waveformData = waveform.map(value => max > 0 ? value / max : 0);
      this.waveformReady = true;
      this.waveformError = '';
    } catch {
      if (!abort.signal.aborted) this.waveformError = 'Waveform unavailable. Playback controls are still available.';
    } finally {
      if (context && context.state !== 'closed') await context.close();
      if (this.waveformContext === context) this.waveformContext = undefined;
      if (this.mediaAbort === abort) this.waveformLoading = false;
    }
  }

  private drawWaveform(): void {
    if (!this.showWaveform || !this.waveformReady) return;

    const canvas = this.shadowRoot?.querySelector('canvas') as HTMLCanvasElement;
    if (!canvas) return;

    const container = canvas.parentElement!;
    const dpr = window.devicePixelRatio || 1;
    const width = container.clientWidth;
    const height = container.clientHeight;

    canvas.width = width * dpr;
    canvas.height = height * dpr;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, height);

    const bars = this.waveformData.length;
    if (bars === 0) return;

    const barWidth = width / bars;
    const unplayedColor = getComputedStyle(this).getPropertyValue('--dees-color-text-muted').trim();

    for (let i = 0; i < bars; i++) {
      const amplitude = this.waveformData[i];
      const barHeight = Math.max(2, amplitude * (height - 4));
      const x = i * barWidth;
      const y = (height - barHeight) / 2;

      ctx.fillStyle = unplayedColor;
      ctx.fillRect(x + 0.5, y, barWidth - 1, barHeight);
    }
  }

  private handleWaveformClick(e: MouseEvent): void {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    this.seek(ratio * this.duration);
  }

  private handleVolumeChange(e: Event): void {
    const value = parseFloat((e.target as HTMLInputElement).value);
    this.setVolume(value);
  }

  private formatTime(seconds: number): string {
    if (!isFinite(seconds) || seconds < 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  private cleanup(): void {
    this.mediaAbort?.abort();
    this.waveformLoading = false;
    // Each decode owns and closes its context in loadWaveform's finally block.
    this.playbackError = '';
    this.error = '';
    this.waveformError = '';
    this.loading = false;
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.src = '';
      this.audioElement = null;
    }
    this.isPlaying = false;
    this.currentTime = 0;
    this.duration = 0;
    this.waveformData = [];
    this.waveformReady = false;
  }
}
